import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import LayoutConfig from './components/LayoutConfig';
import StandardGrid from './components/StandardGrid';
import PreviewBox from './components/PreviewBox';

const { FiGrid, FiCopy, FiCheck, FiLayout } = FiIcons;

function App() {
  const [layoutWidth, setLayoutWidth] = useState(1080);
  const [copiedValue, setCopiedValue] = useState(null);

  const specialLayouts = [
    { 
      label: 'Sidebar Layout (Links)', 
      parts: [
        { label: 'SIDEBAR (1/4)', factor: 1/4 },
        { label: 'HAUPTINHALT (3/4)', factor: 3/4 }
      ]
    },
    { 
      label: 'Golden Ratio / Drittel-Mix', 
      parts: [
        { label: 'SCHMAL (1/3)', factor: 1/3 },
        { label: 'BREIT (2/3)', factor: 2/3 }
      ]
    }
  ];

  const copyToClipboard = async (width, id) => {
    try {
      await navigator.clipboard.writeText(width.toString());
      setCopiedValue(id);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <div className="min-h-screen pb-24 font-sans" style={{ backgroundColor: '#F9F9F7' }}>
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 rounded-[14px] flex items-center justify-center mr-4 shadow-lg shadow-orange-100" style={{ backgroundColor: '#FFA22A' }}>
              <SafeIcon icon={FiGrid} className="text-2xl text-white" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight">Image Size Helper</h1>
          </div>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed font-medium">
            Berechne die perfekte Bildbreite für dein Layout. Nie wieder zu große Bilder hochladen.
          </p>
        </motion.div>

        {/* Configuration & Preview Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16 items-stretch">
          <LayoutConfig layoutWidth={layoutWidth} setLayoutWidth={setLayoutWidth} />
          <PreviewBox layoutWidth={layoutWidth} />
        </div>

        {/* Standard Grids */}
        <StandardGrid 
          layoutWidth={layoutWidth} 
          copiedValue={copiedValue} 
          copyToClipboard={copyToClipboard} 
        />

        {/* Mix Grids - High Contrast & Unified Style */}
        <section>
          <div className="flex items-center mb-8">
            <div className="w-10 h-10 rounded-[10px] bg-[#FFA22A] flex items-center justify-center mr-4 shadow-md shadow-orange-100">
              <SafeIcon icon={FiLayout} className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Mix-Layouts</h2>
              <p className="text-sm text-gray-500 font-medium tracking-tight">Spezielle Proportionen & Sidebars</p>
            </div>
            <div className="hidden md:block h-px flex-1 bg-gray-200 ml-8 opacity-50"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {specialLayouts.map((layout, idx) => (
              <motion.div
                key={`special-${idx}`}
                className="bg-white rounded-[10px] p-10 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                <h3 className="text-xs font-black text-[#FFA22A] uppercase tracking-widest mb-8">{layout.label}</h3>
                
                {/* High Contrast Visualization Bar */}
                <div className="flex h-16 gap-3 mb-10">
                  {layout.parts.map((part, pIdx) => (
                    <div 
                      key={pIdx} 
                      className="rounded-[6px] flex items-center justify-center text-xs font-black border-2 transition-all duration-500"
                      style={{ 
                        flex: part.factor,
                        backgroundColor: pIdx === 0 ? '#FFF7ED' : '#F9FAFB',
                        borderColor: pIdx === 0 ? '#FFA22A' : '#E5E7EB',
                        color: pIdx === 0 ? '#EA580C' : '#9CA3AF'
                      }}
                    >
                      {Math.round(part.factor * 100)}%
                    </div>
                  ))}
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-2 gap-6">
                  {layout.parts.map((part, pIdx) => {
                    const width = Math.floor(layoutWidth * part.factor);
                    const id = `special-${idx}-${pIdx}`;
                    const isCopied = copiedValue === id;

                    return (
                      <div key={id} className={`p-6 rounded-[10px] border-2 transition-all ${pIdx === 0 ? 'bg-orange-50/50 border-orange-100' : 'bg-gray-50 border-gray-100'}`}>
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">{part.label}</div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-baseline">
                            <span className="text-3xl font-black text-gray-900">{width}</span>
                            <span className="text-sm font-bold text-gray-400 ml-1">px</span>
                          </div>
                          <button 
                            onClick={() => copyToClipboard(width, id)}
                            className={`p-2.5 rounded-[10px] transition-all ${isCopied ? 'text-white bg-green-500 shadow-lg shadow-green-100' : 'text-gray-400 hover:text-white hover:bg-[#FFA22A] hover:shadow-lg hover:shadow-orange-100'}`}
                          >
                            <SafeIcon icon={isCopied ? FiCheck : FiCopy} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #FFA22A;
          border: 4px solid white;
          box-shadow: 0 4px 12px rgba(255, 162, 42, 0.3);
          cursor: pointer;
          transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

export default App;